import { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { KeyRound, Plus, Copy, Ban, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const copy = (text) => { navigator.clipboard.writeText(text); toast.success('Copied to clipboard'); };
const when = (iso) => (iso ? new Date(iso).toLocaleString('en-IN') : 'Never');

const NewKeyDialog = ({ apiKey, onClose }) => (
  <Dialog open={!!apiKey} onOpenChange={(o) => !o && onClose()}>
    <DialogContent data-testid="new-api-key-dialog">
      <DialogHeader>
        <DialogTitle>Your new API key</DialogTitle>
        <DialogDescription>Copy it now. For security, it will not be shown again.</DialogDescription>
      </DialogHeader>
      <div className="flex items-center gap-2 bg-stone-900 text-sage-200 rounded-md p-3 font-mono text-xs break-all" data-testid="new-api-key-value">
        <span className="flex-1">{apiKey}</span>
        <Button size="sm" variant="ghost" className="text-sage-200 hover:bg-stone-800" onClick={() => copy(apiKey)} data-testid="copy-new-api-key"><Copy className="w-4 h-4" /></Button>
      </div>
      <p className="text-xs text-amber-700 flex gap-1.5"><AlertTriangle className="w-4 h-4 shrink-0" />Keep this key on your website's server (backend). Never put it in browser JavaScript.</p>
      <Button onClick={onClose} className="bg-sage-700 hover:bg-sage-800 text-white" data-testid="close-new-api-key">I have saved it</Button>
    </DialogContent>
  </Dialog>
);

export const ApiKeysTab = () => {
  const [keys, setKeys] = useState([]);
  const [name, setName] = useState('GONomikai Website');
  const [newKey, setNewKey] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const res = await api.get('/api-keys'); setKeys(res.data); }
    catch { toast.error('Failed to load API keys'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const res = await api.post('/api-keys', { name }); setNewKey(res.data.api_key); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed to create key'); }
    finally { setBusy(false); }
  };

  const revoke = async (k) => {
    if (!window.confirm(`Revoke "${k.name}"? Any website using it will stop working immediately.`)) return;
    try { await api.delete(`/api-keys/${k.id}`); toast.success('API key revoked'); load(); }
    catch { toast.error('Failed to revoke key'); }
  };

  return (
    <div className="space-y-5">
      <form onSubmit={create} className="bg-white border border-stone-200 rounded-md p-4 flex flex-col sm:flex-row gap-3 sm:items-end">
        <div className="flex-1">
          <label className="text-sm font-medium text-stone-700">Key name</label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. GONomikai Website" required className="mt-1" data-testid="api-key-name-input" />
        </div>
        <Button type="submit" disabled={busy} className="bg-sage-700 hover:bg-sage-800 text-white" data-testid="generate-api-key-btn"><Plus className="w-4 h-4 mr-2" />Generate API Key</Button>
      </form>

      <div className="space-y-3">
        {keys.length === 0 && <p className="text-stone-500 text-sm" data-testid="api-keys-empty">No API keys yet.</p>}
        {keys.map((k) => (
          <div key={k.id} className={`bg-white border rounded-md p-4 flex items-center gap-4 ${k.active ? 'border-stone-200' : 'border-stone-100 opacity-60'}`} data-testid={`api-key-${k.id}`}>
            <div className="w-10 h-10 rounded-md bg-sage-50 flex items-center justify-center"><KeyRound className="w-5 h-5 text-sage-700" /></div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-stone-800">{k.name} {!k.active && <span className="ml-2 text-xs bg-red-50 text-red-600 px-1.5 py-0.5 rounded">Revoked</span>}</p>
              <p className="font-mono text-xs text-stone-500">{k.key_prefix}</p>
              <p className="text-xs text-stone-400 mt-0.5">Created {when(k.created_at)} · Last used {when(k.last_used_at)} · {k.usage_count || 0} requests</p>
            </div>
            {k.active && <Button variant="ghost" size="sm" onClick={() => revoke(k)} className="text-red-600 hover:bg-red-50" data-testid={`revoke-api-key-${k.id}`}><Ban className="w-4 h-4 mr-1" />Revoke</Button>}
          </div>
        ))}
      </div>
      <NewKeyDialog apiKey={newKey} onClose={() => setNewKey(null)} />
    </div>
  );
};
